import React from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material"
import { useAppDispatch } from "../../../../app/hooks"
import { APIError } from "../../../../app/appTypes"
import { CompanyDocumentType, loadCompanyDocumentTypes } from "../../../administration/companyDocumentTypes/companyDocumentTypesApi"

interface CompanyQMSDocumentTypesSelectProps {
    value: number | ""
    onChange: (value: number | "") => void
    onDocumentTypeChange?: (value: CompanyDocumentType | undefined) => void
    label?: string
    required?: boolean
    disabled?: boolean
    fullWidth?: boolean
    helperText?: string
}

export const CompanyQMSDocumentTypesSelect: React.FC<CompanyQMSDocumentTypesSelectProps> = ({
    value,
    onChange,
    onDocumentTypeChange,
    label = "Document type",
    required = false,
    disabled = false,
    fullWidth = true,
    helperText,
}) => {
    const dispatch = useAppDispatch()
    const [options, setOptions] = React.useState<CompanyDocumentType[]>([])
    const [isLoading, setIsLoading] = React.useState(false)
    const [error, setError] = React.useState<APIError | null>(null)

    React.useEffect(() => {
        setIsLoading(true)
        setError(null)

        dispatch(loadCompanyDocumentTypes())
            .then(unwrapResult)
            .then((items: CompanyDocumentType[]) => {
                const qmsEnabledAndActive = (items ?? [])
                    .filter(item => item.isQmsEnabled && item.isActive)
                    .sort((a, b) => a.displayOrder - b.displayOrder)

                setOptions(qmsEnabledAndActive)
            })
            .catch((e: APIError) => {
                console.error(e)
                setError(e)
            })
            .finally(() => setIsLoading(false))
    }, [dispatch])

    const handleChange = (event: SelectChangeEvent<string>) => {
        const raw = event.target.value
        if (!raw) {
            onChange("")
            onDocumentTypeChange?.(undefined)
            return
        }
        const selectedId = Number(raw)
        const selected = options.find(item => item.id === selectedId)
        onChange(selectedId)
        onDocumentTypeChange?.(selected)
    }

    React.useEffect(() => {
        if (value === "") {
            onDocumentTypeChange?.(undefined)
            return
        }

        const selected = options.find(item => item.id === value)
        onDocumentTypeChange?.(selected)
    }, [options, value, onDocumentTypeChange])

    const selectValue = value === "" ? "" : String(value)
    const hasError = !!error

    return (
        <FormControl fullWidth={fullWidth} required={required} disabled={disabled || isLoading} error={hasError}>
            <InputLabel id="company-qms-document-type-select-label">{label}</InputLabel>
            <Select
                labelId="company-qms-document-type-select-label"
                id="company-qms-document-type-select"
                value={selectValue}
                label={label}
                onChange={handleChange}
                displayEmpty
            >
                {!required && <MenuItem value="">-</MenuItem>}
                {options.map(item => (
                    <MenuItem key={item.id} value={String(item.id)}>
                        {item.title}
                    </MenuItem>
                ))}
            </Select>

            {hasError && <FormHelperText>Failed to load document types.</FormHelperText>}
            {!hasError && helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
}
